import dayjs from "dayjs";
import { getLastNDaysLabels } from "../../utils/helper";

export const getBMRChartData = (chartData = [], period) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const dataMap = new Map();
  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");
    const value = parseFloat(entry.result?.replace(" Kcal", "") || "0");
    if (!dataMap.has(label)) {
      dataMap.set(label, value);
    }
  });

  const finalData = labels.map((label) => ({
    label,
    value: dataMap.get(label) ?? 0,
  }));

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "BMR",
        data: finalData.map((d) => d.value),
        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "var(--primaryColor)",
        pointRadius: function (context) {
          return context.raw === 0 ? 0.5 : 5;
        },
        pointHoverRadius: function (context) {
          return context.raw === 0 ? 5 : 6;
        },
      },
    ],
  };
};

export const getBMRStats = (chartData = []) => {
  const bmrValues = chartData.map((item) =>
    parseFloat(item.result.toString().replace(/[^\d.]/g, "")),
  );

  if (bmrValues.length === 0) {
    return { min: 0, max: 5000, stepSize: 500 };
  }

  const maxValue = Math.max(...bmrValues);

  let stepSize = 250;
  if (maxValue > 0 && maxValue < 1000) stepSize = 100;
  else if (maxValue >= 1000 && maxValue <= 2000) stepSize = 500;
  else if (maxValue > 2000 && maxValue <= 3000) stepSize = 1000;
  else if (maxValue > 3000) stepSize = 1250;

  const max = Math.ceil(maxValue / stepSize) * stepSize;

  return {
    min: 0,
    max,
    stepSize,
  };
};

export const getORMChartData = (chartData = [], period) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const liftMap = new Map();

  filteredChartData.forEach((entry) => {
    const lift = entry.lift;
    const label = dayjs(entry.createdAt).format("MMM D");
    const result = parseFloat(entry.result);

    if (!liftMap.has(lift)) {
      liftMap.set(lift, new Map());
    }

    const liftData = liftMap.get(lift);
    if (!liftData.has(label)) {
      liftData.set(label, result);
    }
  });

  const datasets = Array.from(liftMap.entries()).map(([lift, dateMap]) => {
    const data = labels.map((label) => dateMap.get(label) ?? 0);
    return {
      label: lift,
      data,
      borderColor: getLiftColor(lift),
      backgroundColor: getLiftColor(lift),
      tension: 0.4,
      pointRadius: function (context) {
        return context.raw === 0 ? 0 : 5;
      },
      pointHoverRadius: function (context) {
        return context.raw === 0 ? 5 : 6;
      },
    };
  });

  return {
    labels,
    datasets,
  };
};

const liftColors = {
  "Bench Press": "#a259ff",
  Squat: "#3a86ff",
  Deadlift: "#ffbe0b",
  "Overhead Press": "#80ed99",
  "Barbell Row": "#00f5d4",
};

const getLiftColor = (lift) => {
  return liftColors[lift] || "#ccc";
};

export const getORMStats = (chartData = []) => {
  const ormValues = chartData
    .map((item) => parseFloat(item.result?.toString().replace(/[^\d.]/g, "")))
    .filter((value) => !isNaN(value));

  if (ormValues.length === 0) {
    return { ormMin: 0, ormMax: 100, ormStepSize: 20 };
  }

  const maxValue = Math.max(...ormValues);
  const minValue = Math.min(...ormValues);

  let ormStepSize = 10;

  if (maxValue <= 20) ormStepSize = 2;
  else if (maxValue <= 50) ormStepSize = 5;
  else if (maxValue <= 100) ormStepSize = 10;
  else if (maxValue <= 200) ormStepSize = 20;
  else if (maxValue <= 300) ormStepSize = 25;
  else ormStepSize = 50;

  const ormMax = Math.ceil(maxValue / ormStepSize) * ormStepSize;
  const ormMin = Math.floor(minValue / ormStepSize) * ormStepSize;

  return {
    ormMin,
    ormMax,
    ormStepSize,
  };
};

export const getWHRChartData = (chartData = [], period, gender) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const startDate = dayjs()
    .subtract(days - 1, "day")
    .startOf("day");
  const endDate = dayjs().endOf("day");

  const filteredChartData = chartData.filter((entry) => {
    const entryDate = dayjs(entry.createdAt);

    return (
      entry.gender === gender &&
      entryDate.isAfter(startDate.subtract(1, "day")) &&
      entryDate.isBefore(endDate.add(1, "day"))
    );
  });

  const dataMap = new Map();

  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D"); // matches labels
    const value = parseFloat(entry.result || "0");

    if (!dataMap.has(label)) {
      dataMap.set(label, []);
    }
    dataMap.get(label).push(value);
  });

  const finalData = labels.map((label) => {
    const values = dataMap.get(label) || [];

    const avg =
      values.length > 0
        ? values.reduce((a, b) => a + b, 0) / values.length
        : null; 

    return {
      label,
      value: avg !== null ? Number(avg.toFixed(2)) : null,
    };
  });

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "WHR",
        data: finalData.map((d) => d.value),

        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },

        fill: true,
        tension: 0.4,

        spanGaps: true, // connect line
        pointRadius: (ctx) => (ctx.raw === null ? 0 : 5),
        pointHoverRadius: 6,
        pointBackgroundColor: "var(--primaryColor)",
      },
    ],
  };
};

export const getWHRStats = (chartData = [], gender, period) => {
  const days = period === "month" ? 30 : 7;

  const startDate = dayjs()
    .subtract(days - 1, "day")
    .startOf("day");
  const endDate = dayjs().endOf("day");

  const filteredChartData = chartData.filter((entry) => {
    const entryDate = dayjs(entry.createdAt);

    return (
      entry.gender === gender &&
      entryDate.isAfter(startDate.subtract(1, "day")) &&
      entryDate.isBefore(endDate.add(1, "day"))
    );
  });

  const whrValues = filteredChartData
    .map((item) => parseFloat(item.result))
    .filter((value) => !isNaN(value));

  const zoneThresholds = gender === "male" ? [0.0, 0.9, 1.0] : [0.0, 0.8, 0.85];

  const allValues = [...whrValues, ...zoneThresholds];

  if (allValues.length === 0) {
    return { whrMin: 0, whrMax: 1.2, whrStepSize: 0.1 };
  }

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  let whrStepSize = 0.1;

  if (maxValue <= 1) whrStepSize = 0.1;
  else if (maxValue <= 2) whrStepSize = 0.2;
  else whrStepSize = 0.5;

  const whrMin = Math.floor(minValue / whrStepSize) * whrStepSize;
  const whrMax = Math.ceil(maxValue / whrStepSize) * whrStepSize;

  return {
    whrMin,
    whrMax,
    whrStepSize,
  };
};

export const getBMIChartData = (chartData = [], period) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const dataMap = new Map();
  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");

    const numericBMI = parseFloat(entry.result?.match(/[\d.]+/)?.[0] || "0");

    if (!dataMap.has(label)) {
      dataMap.set(label, numericBMI);
    }
  });

  const finalData = labels.map((label) => ({
    label,
    value: dataMap.get(label) ?? 0,
  }));

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "BMI",
        data: finalData.map((d) => d.value),
        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "var(--primaryColor)",
        pointRadius: function (context) {
          return context.raw === 0 ? 0.5 : 5;
        },
        pointHoverRadius: function (context) {
          return context.raw === 0 ? 5 : 6;
        },
      },
    ],
  };
};

export const getBMIStats = (chartData = []) => {
  const bmiValues = chartData.map((item) =>
    parseFloat(item.result?.toString().match(/[\d.]+/)?.[0] || "0"),
  );

  if (bmiValues.length === 0) {
    return { bmiMin: 0, bmiMax: 40, bmiStepSize: 5 };
  }

  const maxValue = Math.max(...bmiValues);

  let stepSize = 2;
  if (maxValue <= 20) stepSize = 2;
  else if (maxValue > 20 && maxValue <= 30) stepSize = 5;
  else if (maxValue > 30) stepSize = 10;

  const max = Math.ceil(maxValue / stepSize) * stepSize;

  return {
    bmiMin: 0,
    bmiMax: max,
    bmiStepSize: stepSize,
  };
};

export const getBFPChartData = (chartData = [], period, gender) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter(
    (entry) =>
      entry.gender === gender &&
      dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const dataMap = new Map();
  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");
    const value = parseFloat(entry.result || "0");
    if (!dataMap.has(label)) {
      dataMap.set(label, value);
    }
  });

  const finalData = labels.map((label) => ({
    label,
    value: dataMap.get(label) ?? 0,
  }));

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "BFP",
        data: finalData.map((d) => d.value),
        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "var(--primaryColor)",
        pointRadius: function (context) {
          return context.raw === 0 ? 0.5 : 5;
        },
        pointHoverRadius: function (context) {
          return context.raw === 0 ? 5 : 6;
        },
      },
    ],
  };
};

export const getBFPStats = (chartData = []) => {
  const bfpValues = chartData.map((item) =>
    parseFloat(item.result.toString().replace(/[^\d.]/g, "")),
  );

  if (bfpValues.length === 0) {
    return { bfpMin: 0, bfpMax: 50, bfpStepSize: 5 };
  }

  const maxValue = Math.max(...bfpValues);

  let stepSize = 5;
  if (maxValue > 0 && maxValue <= 20) stepSize = 2;
  else if (maxValue > 20 && maxValue <= 40) stepSize = 5;
  else if (maxValue > 40 && maxValue <= 60) stepSize = 10;
  else if (maxValue > 60) stepSize = 20;

  const max = Math.ceil(maxValue / stepSize) * stepSize;

  return {
    bfpMin: 0,
    bfpMax: max,
    bfpStepSize: stepSize,
  };
};

export const getMacroChartData = (chartData = [], period, gender, t) => {
  if (!Array.isArray(chartData)) return { labels: [], datasets: [] };

  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const fatData = new Array(labels.length).fill(0);
  const proteinData = new Array(labels.length).fill(0);
  const carbsData = new Array(labels.length).fill(0);

  const filteredChartData = chartData.filter(
    (entry) =>
      entry.gender === gender &&
      dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  filteredChartData.forEach((entry) => {
    const dateLabel = dayjs(entry.createdAt).format("MMM D");
    const index = labels.indexOf(dateLabel);

    if (index !== -1 && entry.result) {
      fatData[index] += parseFloat(entry.result.fat) || 0;
      proteinData[index] += parseFloat(entry.result.protein) || 0;
      carbsData[index] += parseFloat(entry.result.carbs) || 0;
    }
  });

  return {
    labels,
    datasets: [
      {
        label: t("fat"),
        data: fatData,
        backgroundColor: "#f6c244",
        grouped: true,
        borderWidth: 1,
        barThickness: period === "week" ? 60 : 25,
      },
      {
        label: t("protein"),
        data: proteinData,
        backgroundColor: "#a879e6",
        grouped: true,
        borderWidth: 1,
        barThickness: period === "week" ? 60 : 25,
      },
      {
        label: t("carbs"),
        data: carbsData,
        backgroundColor: "#3ddc91",
        grouped: true,
        borderWidth: 1,
        barThickness: period === "week" ? 60 : 25,
      },
    ],
  };
};

export const getMacroStats = (chartData = []) => {
  const dailyTotals = {};

  chartData.forEach((item) => {
    if (!item.result) return;

    const date = dayjs(item.createdAt).format("MMM D");

    const fat = parseFloat(item.result.fat) || 0;
    const protein = parseFloat(item.result.protein) || 0;
    const carbs = parseFloat(item.result.carbs) || 0;

    if (!dailyTotals[date]) dailyTotals[date] = 0;

    dailyTotals[date] += fat + protein + carbs;
  });

  const totals = Object.values(dailyTotals);

  if (totals.length === 0) {
    return { macroMin: 0, macroMax: 200, macroStepSize: 50 };
  }

  const maxValue = Math.max(...totals);

  let macroStepSize = 50;

  if (maxValue <= 100) macroStepSize = 20;
  else if (maxValue <= 300) macroStepSize = 50;
  else if (maxValue <= 600) macroStepSize = 100;
  else macroStepSize = 200;

  const macroMax = Math.ceil(maxValue / macroStepSize) * macroStepSize;

  return {
    macroMin: 0,
    macroMax,
    macroStepSize,
  };
};

export const getHydrationChartData = (chartData = [], period) => {
  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const dataMap = new Map();
  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");
    const value =
      entry.result && typeof entry.result === "string"
        ? parseFloat(entry.result.split(" L")[0])
        : 0;
    if (!dataMap.has(label)) {
      dataMap.set(label, value);
    }
  });

  const finalData = labels.map((label) => ({
    label,
    value: dataMap.get(label) ?? 0,
  }));

  return {
    labels: finalData.map((d) => d.label),
    datasets: [
      {
        label: "Hydration (L)",
        data: finalData.map((d) => d.value),
        borderColor: "var(--primaryColor)",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(162, 110, 255, 0.3)");
          gradient.addColorStop(1, "rgba(162, 110, 255, 0)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "var(--primaryColor)",
        pointRadius: function (context) {
          return context.raw === 0 ? 0.5 : 5;
        },
        pointHoverRadius: function (context) {
          return context.raw === 0 ? 5 : 6;
        },
      },
    ],
  };
};

export const getHydrationStats = (chartData = []) => {
  const hydValues = chartData.map((item) => {
    const resultStr = typeof item.result === "string" ? item.result : "0";
    return parseFloat(resultStr.split(" L")[0] || "0");
  });

  if (hydValues.length === 0) {
    return { hydMin: 0, hydMax: 5, hydStepSize: 0.5 };
  }

  const maxValue = Math.max(...hydValues);

  let hydStepSize = 0.5;
  if (maxValue > 0 && maxValue <= 2) hydStepSize = 0.5;
  else if (maxValue > 2 && maxValue <= 4) hydStepSize = 1;
  else if (maxValue > 4) hydStepSize = 2;

  const hydMax = Math.ceil(maxValue / hydStepSize) * hydStepSize;

  return {
    hydMin: 0,
    hydMax,
    hydStepSize,
  };
};

export const getHRCChartData = (chartData = [], period, t) => {
  const days = period === "month" ? 30 : 7;

  const filteredChartData = chartData.filter((entry) =>
    dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const dataMap = new Map();
  filteredChartData.forEach((entry) => {
    const label = dayjs(entry.createdAt).format("MMM D");
    if (!dataMap.has(label)) {
      let min = 0;
      let max = 0;

      if (entry?.result && typeof entry.result === "string") {
        const parts = entry.result.split(/[-–]/);

        if (parts.length === 2) {
          min = parseInt(parts[0]?.trim(), 10) || 0;
          max = parseInt(parts[1]?.trim(), 10) || 0;
        }
      }
      dataMap.set(label, {
        min,
        max,
        activity: entry.activity,
        result: entry.result,
      });
    }
  });

  const zones = [
    {
      label: t("zone1Note"),
      from: 50,
      to: 60,
      color: "#3ddc91",
      key: "light",
    },
    {
      label: t("zone2Note"),
      from: 60,
      to: 70,
      color: "#f6c244",
      key: "fat_burn",
    },
    {
      label: t("zone3Note"),
      from: 70,
      to: 80,
      color: "#f28500",
      key: "cardio",
    },
    {
      label: t("zone4Note"),
      from: 80,
      to: 90,
      color: "#d32f2f",
      key: "peak",
    },
  ];

  return {
    labels: [""],
    datasets: zones.map((zone, index) => ({
      label: zone.label,
      data: [zone.to - zone.from],
      backgroundColor: zone.color,
      stack: "heartZone",
      barThickness: 40,
      categoryPercentage: 1,
      barPercentage: 1,
      borderWidth: 0,
      borderSkipped: false,
      borderRadius:
        index === 0
          ? { topLeft: 20, bottomLeft: 20, topRight: 0, bottomRight: 0 }
          : index === zones.length - 1
            ? { topLeft: 0, bottomLeft: 0, topRight: 20, bottomRight: 20 }
            : 0,
      activityKey: zone.key,
    })),
    rawEntries: filteredChartData,
  };
};

export const getIBWChartData = (chartData = [], period, gender) => {
  if (!Array.isArray(chartData)) return { labels: [], datasets: [] };

  const days = period === "month" ? 30 : 7;
  const labels = getLastNDaysLabels(days);

  const robinsonData = new Array(labels.length).fill(0);
  const millerData = new Array(labels.length).fill(0);
  const hamwiData = new Array(labels.length).fill(0);

  const filteredChartData = chartData.filter(
    (entry) =>
      entry.gender === gender &&
      dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  filteredChartData.forEach((entry) => {
    const dateLabel = dayjs(entry.createdAt).format("MMM D");
    const index = labels.indexOf(dateLabel);

    if (index !== -1 && entry.result_range?.length > 0) {
      const range = entry.result_range[0];

      robinsonData[index] += parseFloat(range.robinson) || 0;
      millerData[index] += parseFloat(range.miller) || 0;
      hamwiData[index] += parseFloat(range.hamwi) || 0;
    }
  });

  return {
    labels,
    datasets: [
      {
        label: "Robinson",
        data: robinsonData,
        backgroundColor: "#f6c244",
        grouped: true,
        borderWidth: 1,
        barThickness: 30,
      },
      {
        label: "Miller",
        data: millerData,
        backgroundColor: "#a879e6",
        grouped: true,
        borderWidth: 1,
        barThickness: 30,
      },
      {
        label: "Hamwi",
        data: hamwiData,
        backgroundColor: "#3ddc91",
        grouped: true,
        borderWidth: 1,
        barThickness: 30,
      },
    ],
  };
};

export const getIBWStats = (chartData = [], period, gender) => {
  if (!Array.isArray(chartData)) {
    return { ibwMin: 0, ibwMax: 100, ibwStepSize: 20 };
  }

  const days = period === "month" ? 30 : 7;

  const filteredChartData = chartData.filter(
    (entry) =>
      entry.gender === gender &&
      dayjs(entry.createdAt).isAfter(dayjs().subtract(days, "day")),
  );

  const ibwValues = filteredChartData.flatMap((item) => {
    if (!item.result_range?.length) return [];

    const range = item.result_range[0];

    return [
      parseFloat(range.robinson) || 0,
      parseFloat(range.miller) || 0,
      parseFloat(range.hamwi) || 0,
    ];
  });

  if (ibwValues.length === 0) {
    return { ibwMin: 0, ibwMax: 100, ibwStepSize: 20 };
  }

  const maxValue = Math.max(...ibwValues);

  let ibwStepSize = 10;

  if (maxValue <= 100) ibwStepSize = 20;
  else if (maxValue <= 300) ibwStepSize = 50;
  else if (maxValue <= 600) ibwStepSize = 100;
  else ibwStepSize = 100;

  const ibwMax = Math.ceil(maxValue / ibwStepSize) * ibwStepSize;

  return {
    ibwMin: 0,
    ibwMax,
    ibwStepSize,
  };
};
